import React, { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useVariables } from "../store/VariableStore";

const CATEGORY_LABELS = {
  time: "Time",
  id: "ID",
  number: "Number",
  string: "String",
  person: "Person",
  internet: "Internet",
  location: "Location",
};

const SCOPE_STYLES = {
  global: "bg-amber-100 text-amber-700",
  environment: "bg-green-100 text-green-700",
  dynamic: "bg-purple-100 text-purple-700",
};

const VariableAutocomplete = ({ query, position, onSelect, onClose }) => {
  const { getAllVariables, dynamicProviders } = useVariables();
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef(null);

  const q = (query || "").toLowerCase().replace(/^\$/, "");

  const userVars = getAllVariables.filter((v) => v.name.toLowerCase().includes(q));

  const dynamicVars = dynamicProviders.filter(
    (p) => p.name.toLowerCase().includes(q) || p.description.toLowerCase().includes(q),
  );

  const dynamicByCategory = dynamicVars.reduce((acc, p) => {
    const cat = p.category || "string";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(p);
    return acc;
  }, {});

  const flat = [
    ...userVars.map((v) => ({
      label: v.name,
      insert: v.name,
      scope: v.scope,
      description: v.value,
    })),
    ...dynamicVars.map((p) => ({
      label: p.name,
      insert: p.name,
      scope: "dynamic",
      description: p.description,
    })),
  ];

  const hasSuggestions = flat.length > 0;

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!hasSuggestions) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, flat.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        if (flat[activeIndex]) onSelect(flat[activeIndex].insert);
      } else if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [hasSuggestions, flat, activeIndex, onSelect, onClose]);

  useEffect(() => {
    if (!listRef.current) return;
    const item = listRef.current.querySelector(`[data-index="${activeIndex}"]`);
    if (item) item.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  if (!hasSuggestions) return null;

  const envVars = userVars.filter((v) => v.scope === "environment");
  const globalVars = userVars.filter((v) => v.scope === "global");

  const dropdown = (
    <div
      ref={listRef}
      className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-xl max-h-72 overflow-y-auto"
      style={{
        left: Math.max(10, Math.min(position.x, window.innerWidth - 300)),
        top: position.y,
        minWidth: "280px",
        maxWidth: "360px",
      }}
      onMouseDown={(e) => e.preventDefault()}
    >
      {envVars.length > 0 && (
        <SuggestionGroup
          title="Environment"
          items={envVars.map((v) => ({
            label: v.name,
            insert: v.name,
            scope: "environment",
            description: v.description,
          }))}
          flat={flat}
          activeIndex={activeIndex}
          onSelect={onSelect}
          onHover={setActiveIndex}
        />
      )}

      {globalVars.length > 0 && (
        <SuggestionGroup
          title="Global"
          items={globalVars.map((v) => ({
            label: v.name,
            insert: v.name,
            scope: "global",
            description: v.description,
          }))}
          flat={flat}
          activeIndex={activeIndex}
          onSelect={onSelect}
          onHover={setActiveIndex}
        />
      )}

      {Object.entries(dynamicByCategory).map(([cat, providers]) => (
        <SuggestionGroup
          key={cat}
          title={CATEGORY_LABELS[cat] || cat}
          items={providers.map((p) => ({
            label: p.name,
            insert: p.name,
            scope: "dynamic",
            description: p.description,
          }))}
          flat={flat}
          activeIndex={activeIndex}
          onSelect={onSelect}
          onHover={setActiveIndex}
        />
      ))}
    </div>
  );

  return createPortal(dropdown, document.body);
};

const SuggestionGroup = ({ title, items, flat, activeIndex, onSelect, onHover }) => (
  <div>
    <div className="px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wider bg-gray-50 border-b border-gray-100 sticky top-0">
      {title}
    </div>
    {items.map((item) => {
      const flatIdx = flat.findIndex((f) => f.insert === item.insert && f.scope === item.scope);
      const isActive = flatIdx === activeIndex;
      const scopeStyle = SCOPE_STYLES[item.scope] || "";

      return (
        <div
          key={`${item.scope}-${item.insert}`}
          data-index={flatIdx}
          className={`px-3 py-2 cursor-pointer border-b border-gray-50 last:border-0 transition-colors ${
            isActive ? "bg-blue-50" : "hover:bg-gray-50"
          }`}
          onMouseEnter={() => onHover(flatIdx)}
          onClick={() => onSelect(item.insert)}
        >
          <div className="flex items-center justify-between gap-2">
            <span className="font-mono text-sm text-gray-800 truncate">{item.label}</span>
            <span
              className={`px-1.5 py-0.5 rounded text-xs font-medium flex-shrink-0 ${scopeStyle}`}
            >
              {item.scope}
            </span>
          </div>
          {item.description && (
            <div className="text-xs text-gray-400 mt-0.5 truncate">{item.description}</div>
          )}
        </div>
      );
    })}
  </div>
);

export default VariableAutocomplete;
